import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import type { AnalyticsTabParams } from '@/hooks/analytics/analytics-queries';
import { useOverviewTabData } from '@/hooks/analytics/use-overview-tab-data';
import { appShare } from '@/lib/app-share';
import type { AnalyticsTab } from '@/lib/analytics-tabs';
import type { ChartGranularity } from '@/lib/chart-axis';
import { bucketSumChartData } from '@/lib/hooks/use-chart-data';
import type { DeltaView } from '@/lib/period-comparison';
import { formatCurrency, formatNumber } from '@/components/metrics/formatters';
import { ComparisonLabel } from '@/components/metrics/delta-badge';
import type { SparklineRow } from '@/components/home/home-sparkline';
import { OverviewCard } from '@/components/analytics/overview-card';

export interface OverviewTabProps extends AnalyticsTabParams {
  /** Opens another tab ("Ver vendas", …). */
  onOpenTab: (tab: AnalyticsTab) => void;
}

const DASH = '—';
const NO_DELTA: DeltaView = { kind: 'none' };

function percent1(ratio: number, locale: Locales): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(ratio);
}

function sparkRows<T extends { date: string }>(
  points: T[] | undefined,
  granularity: ChartGranularity,
  locale: Locales,
  valueOf: (point: T) => number
): SparklineRow[] {
  return bucketSumChartData(
    points ?? [],
    granularity,
    locale,
    valueOf,
    'value'
  ).map((row) => ({
    month: row.month,
    date: String(row.date),
    value: Number(row.value),
  }));
}

/**
 * Resumo (ANLT-02): Vendas, Push, Usuários and Pedidos, each with its headline
 * number (variation and minigráfico where the period has one), secondary
 * numbers and a shortcut to the tab. 2×2 from 1180px, one column below.
 */
export function OverviewTab({ onOpenTab, ...params }: OverviewTabProps) {
  const { LL, locale } = useI18nContext();
  const data = useOverviewTabData(params);
  const { granularity } = params;
  const range = { from: params.fromDate ?? '', to: params.toDate ?? '' };

  const sales = data.sales.current.data;
  const campaigns = data.campaigns.current.data;
  const users = data.users.current.data;
  const completion = data.ordersCompletion.data;
  const currency = sales?.currency ?? 'USD';
  const pushHidden =
    data.dataSufficiency.data?.campaignsAnalytics.canDisplay === false;
  const activeUsersHidden =
    data.dataSufficiency.data?.activeUsersChart.canDisplay === false;

  const salesSpark = useMemo(
    () =>
      sparkRows(
        data.salesChart.data?.points,
        granularity,
        locale,
        (p) => p.grossRevenueUsd
      ),
    [data.salesChart.data, granularity, locale]
  );
  const pushSpark = useMemo(
    () =>
      sparkRows(
        data.campaignOrdersChart.data?.points,
        granularity,
        locale,
        (p) => p.revenue
      ),
    [data.campaignOrdersChart.data, granularity, locale]
  );
  const downloadsSpark = useMemo(
    () =>
      sparkRows(
        data.appDownloadsChart.data?.points,
        granularity,
        locale,
        (p) => p.downloadsCount
      ),
    [data.appDownloadsChart.data, granularity, locale]
  );

  const comparison = (
    <ComparisonLabel preset={params.preset} now={DateTime.now()} />
  );
  const money = (value: number | undefined) =>
    value === undefined ? DASH : formatCurrency(value, locale, currency);
  const count = (value: number | undefined) =>
    value === undefined ? DASH : formatNumber(value, locale);
  /** Headline: skeleton while loading, "—" when it failed. */
  const headline = (
    query: { isError: boolean },
    value: string | undefined
  ): string | null =>
    value !== undefined ? value : query.isError ? DASH : null;

  const share = sales ? appShare(sales.totalRevenue, sales.web.revenue) : null;
  const completionTotal = completion
    ? completion.completedCount + completion.notCompletedCount
    : 0;

  return (
    <div
      data-analytics-overview
      className="grid grid-cols-1 gap-5 max-md:gap-4 min-[1180px]:grid-cols-2"
    >
      <OverviewCard
        index="01"
        title={LL.analyticsOverview.sales.title()}
        helpKey="overviewSales"
        label={LL.analyticsOverview.sales.label()}
        value={headline(data.sales.current, sales && money(sales.totalRevenue))}
        delta={data.sales.delta((d) => d.totalRevenue)}
        comparison={comparison}
        spark={{ rows: salesSpark, range, granularity }}
        rows={[
          {
            label: LL.analyticsOverview.sales.orders(),
            value: count(sales?.totalPaidOrders),
          },
          {
            label: LL.analyticsOverview.sales.averageTicket(),
            value: sales && sales.totalPaidOrders > 0 ? money(sales.aov) : DASH,
          },
          {
            label: LL.analyticsOverview.sales.appShare(),
            value: share === null ? DASH : percent1(share, locale),
          },
        ]}
        cta={LL.analyticsOverview.sales.cta()}
        onCta={() => onOpenTab('sales')}
      />
      <OverviewCard
        index="02"
        title={LL.analyticsOverview.campaigns.title()}
        helpKey="overviewCampaigns"
        label={LL.analyticsOverview.campaigns.label()}
        value={
          pushHidden
            ? DASH
            : headline(
                data.campaigns.current,
                campaigns && money(campaigns.totalRevenue)
              )
        }
        delta={
          pushHidden ? NO_DELTA : data.campaigns.delta((d) => d.totalRevenue)
        }
        comparison={comparison}
        spark={{ rows: pushSpark, range, granularity }}
        rows={[
          {
            label: LL.analyticsOverview.campaigns.campaigns(),
            value: count(campaigns?.sentCampaignsCount),
          },
          {
            label: LL.analyticsOverview.campaigns.averageCtr(),
            value:
              pushHidden || !campaigns
                ? DASH
                : percent1(campaigns.averageCtr, locale),
          },
          {
            label: LL.analyticsOverview.campaigns.orders(),
            value: pushHidden ? DASH : count(campaigns?.totalOrderPaidCount),
          },
        ]}
        cta={LL.analyticsOverview.campaigns.cta()}
        onCta={() => onOpenTab('campaigns')}
      />
      <OverviewCard
        index="03"
        title={LL.analyticsOverview.users.title()}
        helpKey="overviewUsers"
        label={LL.analyticsOverview.users.label()}
        value={headline(data.users.current, users && count(users.downloads))}
        delta={data.users.delta((d) => d.downloads)}
        comparison={comparison}
        spark={{ rows: downloadsSpark, range, granularity }}
        rows={[
          {
            label: LL.analyticsOverview.users.activeUsers(),
            value: activeUsersHidden ? DASH : count(users?.activeUsers),
          },
          {
            label: LL.analyticsOverview.users.notifications(),
            value: count(users?.usersWithEnabledNotifications.count),
          },
          {
            label: LL.analyticsOverview.users.converted(),
            value: users
              ? percent1(users.convertedDownloads.percentage, locale)
              : DASH,
          },
        ]}
        cta={LL.analyticsOverview.users.cta()}
        onCta={() => onOpenTab('users')}
      />
      <OverviewCard
        index="04"
        title={LL.analyticsOverview.orders.title()}
        helpKey="overviewOrders"
        label={LL.analyticsOverview.orders.label()}
        value={headline(
          data.ordersCompletion,
          completion && count(completion.completedCount)
        )}
        rows={[
          {
            label: LL.analyticsOverview.orders.completed(),
            value:
              completionTotal > 0 && completion
                ? new Intl.NumberFormat(locale, { style: 'percent' }).format(
                    completion.completedCount / completionTotal
                  )
                : DASH,
          },
          {
            label: LL.analyticsOverview.orders.notCompleted(),
            value: count(completion?.notCompletedCount),
          },
        ]}
        cta={LL.analyticsOverview.orders.cta()}
        onCta={() => onOpenTab('orders')}
      />
    </div>
  );
}
